import { Component, Element, Event, EventEmitter, Fragment, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import { handleButtonKeydown } from '../../utils/utils';

type Modifier = 'control' | 'text' | 'checkbox' | 'radio' | 'select' | 'me-card-radio' | 'textarea';
type Selectors = string;

export interface ValidityChange {
  validity: ValidityState;
  validationMessage: string;
}

@Component({
  tag: 'me-form-control',
  styleUrl: 'me-form-control.scss',
  shadow: false,
})
export class MeFormControl {

  @Element() host: HTMLMeFormControlElement;

  @Event() meFormControlValidityChange: EventEmitter<ValidityChange>;

  /**
   * The Form Element Label
   *
   * Renders a label for the given form element when present.
   */
  @Prop() label?: string;

  /**
   * The Form Element Description
   *
   * Renders descriptive text for the given form element when present.
   */
  @Prop() description?: string;

  /**
   * The Form Control Size
   */
  @Prop() size = 'r';

  @Prop() validation = true;

  /**
   * The Custom Error Messages
   *
   * Allows customizing HTML validation API error messages.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
   */
  @Prop() badInputMessage?: string;
  @Prop() patternMismatchMessage?: string;
  @Prop() rangeOverflowMessage?: string;
  @Prop() rangeUnderflowMessage?: string;
  @Prop() stepMismatchMessage?: string;
  @Prop() tooLongMessage?: string;
  @Prop() tooShortMessage?: string;
  @Prop() typeMismatchMessage?: string;
  @Prop() valueMissingMessage?: string;

  /**
   * Validation Error Messages
   *
   * Updates on form submit; i.e. native error messages will appear even if control is untouched.
   * Updates on input; i.e. initial custom / serverside errors will be gone after first input.
   *
   * @see MeFormControl.handleValidityChange
   */
  @Prop({ mutable: true, reflect: true }) errors?: string;
  @Watch('errors')
  handleErrorsChange(errors) {
    this.isValid = this.isValidationEnabled && !errors;
  }

  /**
   * Whether this control should be validated or not
   */
  private get isValidationEnabled(): boolean {
    if (!this.validation) {
      return false;
    }

    if (hasAssociatedForm(this.formElement)) {
      return !this.formElement.form.noValidate;
    }

    return true;
  }

  /**
   * Whether the current `MeFormControl.formElement` value is invalid or not
   */
  private get isInvalid(): boolean {
    return this.isValidationEnabled && Boolean(this.errors);
  }

  /**
   * Whether the current `MeFormControl.formElement` value is valid or not
   *
   * Initial state is false because untouched form controls should not be highlighted
   * even if their values are perfectly valid …
   */
  @State() isValid = false;

  /**
   * Whether the input should be `input[type=password]` or `input[type=text]`;
   * only relevant when `MeFormControl.isPassword`.
   *
   * @see MeFormControl.isPassword
   * @see MeFormControl.componentWillLoad
   */
  @State() private isObfuscatedPassword = false;
  @Watch('isObfuscatedPassword')
  handledIsObfuscatedPasswordChange(isObfuscated) {
    this.formElement?.setAttribute('type', isObfuscated ? 'password' : 'text');
  }

  /**
   * Whether `MeFormControl.formElement` was `input[type=password]` on load
   *
   * The type may change when the user toggles the obfuscated modifier.
   * Therefore, we need to cache the initial type here …
   *
   * @see MeFormControl.isObfuscatedPassword
   * @see MeFormControl.componentWillLoad
   */
  @State() private isPassword = false;

  /**
   * The Form Element
   *
   * @see MeFormControl.componentWillLoad
   */
  private formElement?: HTMLElement;

  /**
   * The Form Element Modifier Class
   *
   * @see MeFormControl.componentWillLoad
   */
  private formElementModifier?: Modifier;

  /**
   * The Form Element Modifiers Map
   *
   * @see MeFormControl.componentWillLoad
   */
  private formElementModifiers: { [K in Modifier]: Selectors } = {
    'control': `
      me-form-control
    `,
    'text': `
      input[type=text],
      input[type=email],
      input[type=tel],
      input[type=url],
      input[type=number],
      input[type=search],
      input[type=password],
      input[type=date]
    `,
    'checkbox': `
      input[type=checkbox]
    `,
    'me-card-radio': `
      me-card-radio input[type=radio]
    `,
    'radio': `
      input[type=radio]
    `,
    'select': `
      select
    `,
    'textarea': `
      textarea
    `
  };

  /**
   * Returns whether the form control should be marked as required or not
   */
  private get isRequired(): boolean {
    if (
      this.formElementModifier === 'control'
      && this.formElement.querySelector('input[required]')
    ) {
      return true;
    }

    if (this.host.parentElement.closest('me-form-control')) {
      return false;
    }

    if (hasRequiredAttribute(this.formElement)) {
      return this.formElement.required;
    }

    return false;
  }

  @Listen('meFormControlValidityChange')
  handleValidityChange(event: CustomEvent<ValidityChange>) {
    if (!this.host.parentElement.closest('me-form-control')) {
      this.updateErrors(event.detail);
    }
  }

  /**
   * Sets initial state depending on the provided slot contents
   *
   * Scans the `<slot />` content to find a form element matching one of the CSS selectors
   * registered in `MeFormControl.formElementModifiers`. Sets `MeFormControl.formElement`
   * and `MeFormControl.formElementModifier` accordingly when an element is found.
   *
   * @see MeFormControl.formElementModifiers
   * @see MeFormControl.formElement
   * @see MeFormControl.formElementModifier
   * @see MeFormControl.isPassword
   * @see MeFormControl.isObfuscatedPassword
   */
  componentWillLoad() {
    for (const [modifier, selectors] of Object.entries(this.formElementModifiers)) {
      this.formElement = this.host.querySelector(selectors)

      if (this.formElement) {
        this.formElementModifier = modifier as Modifier;
        break;
      }
    }

    this.isPassword = Boolean(this.host.querySelector('input[type=password]'));
    this.isObfuscatedPassword = this.isPassword;
  }

  componentDidLoad() {
    this.registerFormElementEventListeners();
  }

  connectedCallback() {
    this.registerFormElementEventListeners();
  }

  disconnectedCallback() {
    this.removeFormElementEventListeners();
  }

  render() {
    const classList = [
      'me-form-control',
      `me-form-control--${this.size}`,
      this.formElementModifier ? 'me-form-control--' + this.formElementModifier : null,
      this.isObfuscatedPassword ? 'me-form-control--obfuscated' : null,
      this.isInvalid ? 'me-form-control--invalid' : null,
      this.isValid ? 'me-form-control--valid' : null,
    ].filter(c => !!c).join(' ');

    return <Host class={classList}>
      {this.labelFragment()}
      <div class="me-form-control__form-element">
        <slot />
        {this.selectIconFragment()}
        {this.textIconsFragment()}
      </div>
      {this.description
        ? <div class="me-form-control__description" innerHTML={this.description}></div>
        : []
      }
      {this.isInvalid
        ? <div class="me-form-control__errors" innerHTML={this.errors}></div>
        : []
      }
    </Host>
  }

  private labelFragment() {
    if (!this.label) {
      return <Fragment />;
    }

    return <Fragment>
      <label class="me-form-control__label" htmlFor={this.formElement?.id}>
        {this.label}
        {this.isRequired
          ? <span class="me-form-control__required-asterisk">*</span>
          : []
        }
      </label>
    </Fragment>;
  }

  private selectIconFragment() {
    if (this.formElementModifier !== 'select') {
      return <Fragment />;
    }

    return <Fragment>
      <div class="me-form-control__icons">
        <div class="me-form-control__chevron-down-icon">
          <i class="fa-solid fa-chevron-down"></i>
        </div>
      </div>
    </Fragment>;
  }

  private textIconsFragment() {
    if (this.formElementModifier !== 'text') {
      return <Fragment />;
    }

    return <Fragment>
      <div class="me-form-control__icons">
        {this.validityIconsFragment()}
        {this.passwordIconsFragment()}
      </div>
    </Fragment>;
  }

  private passwordIconsFragment() {
    if (!this.isPassword) {
      return <Fragment />;
    }

    return <Fragment>
      <div class="me-form-control__eye-icons">
        <div
          class="me-form-control__eye-icon me-form-control__eye-icon--default"
          onClick={() => this.isObfuscatedPassword = !this.isObfuscatedPassword}
          onKeyDown={event => handleButtonKeydown(event, event.target as HTMLElement)}
          role="button"
          tabIndex={0}
          aria-label="Passwort verstecken"
        >
          <i class="fa-regular fa-eye" />
        </div>
        <div
          class="me-form-control__eye-icon me-form-control__eye-icon--slash"
          onClick={() => this.isObfuscatedPassword = !this.isObfuscatedPassword}
          onKeyDown={event => handleButtonKeydown(event, event.target as HTMLElement)}
          role="button"
          tabIndex={0}
          aria-label="Passwort im Klartext anzeigen"
        >
          <i class="fa-regular fa-eye-slash" />
        </div>
      </div>
    </Fragment>;
  }

  private validityIconsFragment() {
    if (!this.isValidationEnabled) {
      return <Fragment />;
    }

    return <Fragment>
      <div class="me-form-control__validity-icons">
        <div class="me-form-control__validity-icon me-form-control__validity-icon--valid">
          <i class="fa-solid fa-check"></i>
        </div>
        <div class="me-form-control__validity-icon me-form-control__validity-icon--invalid">
          <i class="fa-solid fa-circle-exclamation"></i>
        </div>
      </div>
    </Fragment>;
  }

  private registerFormElementEventListeners() {
    this.formElement?.addEventListener('invalid', this.emitValidityChangeEvent.bind(this));
    this.formElement?.addEventListener('input', this.emitValidityChangeEvent.bind(this));
  }

  private removeFormElementEventListeners() {
    this.formElement?.removeEventListener('invalid', this.emitValidityChangeEvent.bind(this));
    this.formElement?.removeEventListener('input', this.emitValidityChangeEvent.bind(this));
  }

  private emitValidityChangeEvent() {
    if (hasValidityState(this.formElement) && hasValidationMessage(this.formElement)) {
      this.meFormControlValidityChange.emit({
        validity: this.formElement.validity,
        validationMessage: this.formElement.validationMessage,
      });
    }
  }

  /**
   * Updates `MeFormControl.errors` using custom error messages;
   * falls back to the native HTML form validation message.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
   */
  private updateErrors({ validity, validationMessage }: ValidityChange) {
    let errors = new Array<string>();

    if (validity.badInput && this.badInputMessage) {
      errors.push(this.badInputMessage);
    }

    if (validity.patternMismatch && this.patternMismatchMessage) {
      errors.push(this.patternMismatchMessage);
    }

    if (validity.rangeOverflow && this.rangeOverflowMessage) {
      errors.push(this.rangeOverflowMessage);
    }

    if (validity.rangeUnderflow && this.rangeUnderflowMessage) {
      errors.push(this.rangeUnderflowMessage);
    }

    if (validity.stepMismatch && this.stepMismatchMessage) {
      errors.push(this.stepMismatchMessage);
    }

    if (validity.tooLong && this.tooLongMessage) {
      errors.push(this.tooLongMessage);
    }

    if (validity.tooShort && this.tooShortMessage) {
      errors.push(this.tooShortMessage);
    }

    if (validity.typeMismatch && this.typeMismatchMessage) {
      errors.push(this.typeMismatchMessage);
    }

    if (validity.valueMissing && this.valueMissingMessage) {
      errors.push(this.valueMissingMessage);
    }

    if (
      errors.length === 0
      && validationMessage
    ) {
      errors.push(validationMessage);
    }

    this.errors = errors.join('<br>');
  }

}

function hasAssociatedForm(element: any): element is { form: HTMLFormElement } {
  return element
    && typeof element === 'object'
    && 'form' in element
    && element.form instanceof HTMLFormElement;
}

function hasValidityState(element: any): element is { validity: ValidityState } {
  return element
    && typeof element === 'object'
    && 'validity' in element
    && element.validity instanceof ValidityState;
}

function hasValidationMessage(element: any): element is { validationMessage: string } {
  return element
    && typeof element === 'object'
    && 'validationMessage' in element
    && typeof element.validationMessage === 'string';
}

function hasRequiredAttribute(element: any): element is { required: boolean } {
  return element
    && typeof element === 'object'
    && 'required' in element
    && typeof element.required === 'boolean';
}
